<template>
  <div
    id="season"
    v-if="season"
    class="mt-3">
    <!-- Season Form -->
    <form
      class="container"
      @submit.prevent="submitSeason(season)">
      <div class="row">
        <div class="col"><small class="text-danger font-italic">All fields required.</small></div>
      </div>

      <div class="row mt-3">
        <div class="col-sm-6">
          <label for="season-name">Season:</label>
          <b-form-input
            id="season-name"
            ref="seasonName"
            name="seasonName"
            type="text"
            placeholder="Enter a season name"
            v-model="season.name"
            :readonly="submitting">
          </b-form-input>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-sm-6">
          <label for="season-start">Start:</label>
          <DateTimePicker
            id="season-start"
            ref="seasonStart"
            name="seasonStart"
            v-model="season.start_date_time"
            :readonly="submitting"
          />
        </div>
      </div>

      <div class="row mt-3">
        <div class="col-sm-6 mt-3 mt-sm-0">
          <label for="season-end">End:</label>
          <DateTimePicker
            id="season-end"
            ref="seasonEnd"
            name="seasonEnd"
            v-model="season.end_date_time"
            :readonly="submitting"
          />
        </div>
      </div>

      <div class="corner-buttons">
        <!-- Save Season Button -->
        <b-button
          class="shadow"
          type="submit"
          variant="success"
          :disabled="submitting">
          <font-awesome-icon icon="save"></font-awesome-icon> Save
        </b-button>
        <!-- / Save Season Button -->

        <!-- Delete Season Button -->
        <b-button
          class="shadow ml-3"
          type="button"
          variant="danger"
          v-if="season.id"
          :disabled="submitting"
          @click="submitSeason(season.id)">
          <font-awesome-icon icon="trash"></font-awesome-icon> Delete Season
        </b-button>
        <!-- / Delete Season Button -->
      </div>
    </form>
    <!-- / Season Form -->
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import EventBus from '../services/event-bus';
import DirtyForm from '../services/dirty-form';
import AppMixin from '../services/app-mixin';

export default {
  name: 'season',
  mixins: [
    DirtyForm,
    AppMixin,
  ],
  data() {
    return {
      season: false,
      seasonCopy: false,
      submitting: false,
    };
  },
  computed: {
    seasonDirty() {
      const seasonJSON = JSON.stringify(this.season);
      const seasonCopyJSON = JSON.stringify(this.seasonCopy);
      return seasonJSON !== seasonCopyJSON;
    },
    anyDirty() {
      return this.seasonDirty;
    },
  },
  methods: {
    submitSeason(season) {
      let seasonPromise;

      this.submitting = true;

      if (typeof season !== 'object') {
        seasonPromise = this.deleteSeason(season);
      } else if (season.id) {
        seasonPromise = this.updateSeason(season);
      } else {
        seasonPromise = this.createSeason(season);
      }

      seasonPromise
        .then(() => {
          this.$router.push({ name: 'seasons' });
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });

      return seasonPromise;
    },
    createSeason(season) {
      this.showToast('Creating season...');

      return DataSvc.createSeason(this.loggedInUser.actingOrg.id, season)
        .then(s => this.delay(1000, s))
        .then((s) => {
          this.seasons = this.seasons.concat([_.cloneDeep(s)]);
          this.season = s;
          this.seasonCopy = _.cloneDeep(s);
          this.showToast('Season created!');
        });
    },
    updateSeason(season) {
      this.showToast('Updating season...');

      return DataSvc.updateSeason(season)
        .then((s) => {
          // update existing seasons
          const sn = _.find(this.seasons, { id: s.id });
          _.extend(sn, s);
          this.season = s;
          this.seasonCopy = _.cloneDeep(s);

          // if acting season was updated
          if (this.loggedInUser.actingSeason
            && this.loggedInUser.actingSeason.id === s.id) {
            this.loggedInUser.actingSeason = _.cloneDeep(s);
          }

          return this.delay(1000);
        })
        .then(() => {
          this.showToast('Season updated!');
        });
    },
    deleteSeason(seasonID) {
      const confirmDelete = () => {
        this.showToast('Deleting season...');

        return DataSvc.deleteSeason(seasonID)
          .then(() => {
            // remove from existing seasons
            this.seasons = _.reject(this.seasons, { id: seasonID });
            return this.delay(1000);
          })
          .then(() => {
            this.showToast('Season deleted!');

            // if acting season was deleted
            if (this.loggedInUser.actingSeason
              && this.loggedInUser.actingSeason.id === seasonID) {
              EventBus.$emit('set-acting-season', { name: 'All Seasons' });
            }
          });
      };

      return this.showConfirm(
        'Are you sure you want to delete this season?',
        'Deleting a season',
      ).then(confirmDelete);
    },
  },
  created() {
    this.showGlobalLoader('Loading season...');

    const initSeason = () => {
      const { seasonID } = this.$route.params;
      let seasonPromise;

      if (seasonID === 'new') {
        seasonPromise = Promise.resolve({});
      } else {
        seasonPromise = DataSvc.getSeason(seasonID);
      }

      // watch for org change and route to events page
      EventBus.$on('acting-org-changed', this.goToSeasons);

      return seasonPromise
        .catch(() => {
          this.showWarning('That season does not exist.');
          this.$router.replace({ name: 'seasons' });
        })
        .then((season) => {
          this.season = season;
          this.seasonCopy = _.cloneDeep(season);

          // focus first input
          this.$nextTick(() => {
            this.focusInput('seasonName');
          });
        });
    };

    this.initPromise.then(initSeason).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-org-changed', this.goToSeasons);
  },
};
</script>
